import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StorefrontService } from "src/app/services/StorefrontService";
import { Basket, money, ProductType } from "./Basket";
import { FanAuthService, FanSession } from "./FanSession";
import { clubTheme } from "./ClubTheme";
import "./Shop.css";
import { ShopHeader } from "./ShopHeader";
import { ShopNav } from "./ShopNav";

type Props = {
	kind: "fixture" | "event";
};

const longDate = (iso: string) => {
	const d = new Date(iso);
	return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "long", weekday: "long" })} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};

/**
 * One fixture or event, and the tickets for it.
 *
 * Fixtures and events share this page because they are the same purchase: pick
 * quantities against ticket types and go. The only genuine difference is what
 * sits at the top — two badges or a poster — so that is the only part that
 * branches.
 *
 * Quantities are staged locally and committed to the basket in one go. A
 * supporter buying four tickets in three types shouldn't get four separate
 * "added to basket" interruptions.
 */
export function ShopProduct({ kind }: Props) {
	const { slug = "", id = "" } = useParams();
	const navigate = useNavigate();
	const [data, setData] = React.useState<any>(null);
	const [error, setError] = React.useState<string | null>(null);
	const [quantities, setQuantities] = React.useState<{ [ticketTypeId: number]: number }>({});
	const [hospitality, setHospitality] = React.useState(0);
	const [access, setAccess] = React.useState<any>(null);
	const [hasSeatMap, setHasSeatMap] = React.useState(false);

	React.useEffect(() => {
		let live = true;
		(async () => {
			try {
				const res = kind === "fixture"
					? await StorefrontService.getFixture(slug, Number(id))
					: await StorefrontService.getEvent(slug, Number(id));
				if (!res.ok) {
					if (live) setError("We can't find that.");
					return;
				}
				const body = await res.json();
				if (live) setData(body);
			} catch {
				if (live) setError("We couldn't load that. Please try again.");
			}
		})();
		return () => { live = false; };
	}, [slug, id, kind]);

	// A priority sale looks different depending on who is asking. The
	// anonymous page says "holders only"; a signed-in holder needs to be told
	// the door is already open to them.
	React.useEffect(() => {
		let live = true;
		if (kind !== "fixture" || !FanSession.signedIn()) return;
		(async () => {
			try {
				const res = await FanAuthService.access(slug, Number(id));
				if (live && res.ok) setAccess(await res.json());
			} catch { /* the general message stands */ }
		})();
		return () => { live = false; };
	}, [slug, id, kind]);

	React.useEffect(() => {
		let live = true;
		if (kind !== "fixture") return;
		(async () => {
			try {
				const res = await StorefrontService.getSeatMap(slug, Number(id));
				if (!live || !res.ok) return;
				const body = await res.json();
				setHasSeatMap(!!body?.hasMap);
			} catch { /* no link, no harm */ }
		})();
		return () => { live = false; };
	}, [slug, id, kind]);

	// Asked separately so the ticket page doesn't wait on it. A club with no
	// hospitality shouldn't see a link to an empty page.
	React.useEffect(() => {
		let live = true;
		if (kind !== "fixture") return;
		(async () => {
			try {
				const res = await StorefrontService.getHospitality(slug, Number(id));
				if (!live || !res.ok) return;
				const body = await res.json();
				setHospitality(body?.packages?.length || 0);
			} catch { /* no link, no harm */ }
		})();
		return () => { live = false; };
	}, [slug, id, kind]);

	if (error) {
		return (
			<div className="shop" style={clubTheme(data?.club)}>
				<div className="shop-result">
					<h2>Not found</h2>
					<p>{error}</p>
				</div>
			</div>
		);
	}

	if (!data) {
		return (
			<div className="shop" style={clubTheme(data?.club)}>
				<div className="shop-result"><span className="shop-spinner" /></div>
			</div>
		);
	}

	const subject = kind === "fixture" ? data.fixture : data.event;
	const onSale = access ? access.canBuy : data.saleStatus === "onsale";
	const types: any[] = data.ticketTypes || [];
	const total = types.reduce((sum, t) => sum + (quantities[t.ticketTypeId] || 0) * t.pricePence, 0);
	const anyPicked = types.some((t) => (quantities[t.ticketTypeId] || 0) > 0);

	// The club's own cap, where it set one. Falls back to 20, which is a
	// sanity bound rather than a rule — it stops a stuck finger, not a tout.
	const maxPerOrder: number = data.maxPerOrder || 20;

	const pickedTotal = types.reduce((sum: number, t: any) => sum + (quantities[t.ticketTypeId] || 0), 0);

	const step = (ticketTypeId: number, by: number) => {
		setQuantities((current) => {
			const current_ = current[ticketTypeId] || 0;
			// Capped across every type, not per type. A limit of four means four
			// tickets, not four of each.
			const others = types.reduce((sum: number, t: any) =>
				sum + (t.ticketTypeId === ticketTypeId ? 0 : (current[t.ticketTypeId] || 0)), 0);
			const next = Math.max(0, Math.min(maxPerOrder - others, current_ + by));
			return { ...current, [ticketTypeId]: next };
		});
	};

	const addToBasket = () => {
		const occasion = kind === "fixture"
			? `${subject.isHome ? "v" : "away to"} ${subject.opponent || "TBC"}`
			: subject.name;
		const when = kind === "fixture" ? subject.kickOff : subject.start;

		for (const type of types) {
			const quantity = quantities[type.ticketTypeId] || 0;
			if (quantity < 1) continue;
			Basket.add(slug, {
				name: type.name,
				occasion,
				pricePence: type.pricePence,
				productId: Number(id),
				productType: kind === "fixture" ? ProductType.Fixture : ProductType.Event,
				quantity,
				ticketTypeId: type.ticketTypeId,
				when
			});
		}
		navigate(`/club/${slug}/basket`);
	};

	const whenLabel = kind === "fixture" ? longDate(subject.kickOff) : longDate(subject.start);

	return (
		<div className="shop has-nav" style={clubTheme(data?.club)}>
			<ShopHeader club={data.club} slug={slug} />
			<div className="shop-inner">
				<div className="shop-hero">
					{kind === "event" && subject.backgroundUrl &&
						<div className="shop-hero-image" style={{ backgroundImage: `url(${subject.backgroundUrl})` }} />}

					<div className="shop-hero-body">
						{kind === "fixture" && <div className="shop-teams">
							<div className="shop-team">
								{data.club?.badgeUrl && <img alt="" src={data.club.badgeUrl} />}
								<span>{data.club?.name}</span>
							</div>
							<span className="shop-vs">vs</span>
							<div className="shop-team">
								{subject.opponentBadgeUrl && <img alt="" src={subject.opponentBadgeUrl} />}
								<span>{subject.opponent || "TBC"}</span>
							</div>
						</div>}

						{kind === "event" && <h2>{subject.name}</h2>}

						<div className="shop-hero-meta">
							{whenLabel}
							{subject.competition ? ` · ${subject.competition}` : ""}
							{subject.venue ? ` · ${subject.venue}` : ""}
						</div>

						{kind === "event" && subject.description &&
							<p className="shop-card-meta" style={{ marginTop: 12 }}>{subject.description}</p>}

						{subject.amenities?.length > 0 &&
							<div className="shop-amenities">
								{subject.amenities.map((a: string) => (
									<span className="shop-amenity" key={a}>{a}</span>
								))}
							</div>}
					</div>
				</div>

				{!onSale && <>
					<div className={data.priorityOnly ? "shop-warn-box" : "shop-error"}>
						{/* The server's message is the specific one — it knows the
						    window's name and when general sale opens. */}
						{access?.reason || data.saleMessage ||
							(data.saleStatus === "scheduled"
								? data.onSaleFrom
									? `Tickets go on sale ${longDate(data.onSaleFrom)}.`
									: "Tickets aren't on sale yet."
								: data.saleStatus === "closed"
									? "Sales have closed for this one."
									: "Tickets aren't on sale online.")}
					</div>

					{/* Only worth suggesting to somebody who isn't signed in — a
					    signed-in fan who still can't buy hasn't got a season ticket,
					    and telling them to log in would be a runaround. */}
					{data.priorityOnly && !FanSession.signedIn() &&
						<button className="shop-btn" onClick={() => navigate(`/club/${slug}/account?next=/club/${slug}/fixture/${id}`)} type="button">
							Sign in &mdash; season ticket holder?
						</button>}
					<button className="shop-btn secondary" onClick={() => navigate(`/club/${slug}`)} type="button">
						Back to what&rsquo;s on
					</button>
				</>}

				{onSale && hasSeatMap &&
					<button className="shop-card shop-hospitality-link" onClick={() => navigate(`/club/${slug}/fixture/${id}/seats`)} type="button">
						<div className="shop-card-body">
							<p className="shop-card-title">Choose your seats</p>
							<div className="shop-card-meta">Pick from the seating plan</div>
						</div>
						<div className="shop-card-right"><span className="shop-price">&rsaquo;</span></div>
					</button>}

				{hospitality > 0 &&
					<button className="shop-card shop-hospitality-link" onClick={() => navigate(`/club/${slug}/fixture/${id}/hospitality`)} type="button">
						<div className="shop-card-body">
							<p className="shop-card-title">Hospitality</p>
							<div className="shop-card-meta">
								{hospitality} {hospitality === 1 ? "package" : "packages"} for this game
							</div>
						</div>
						<div className="shop-card-right"><span className="shop-price">&rsaquo;</span></div>
					</button>}

				{onSale && access?.priorityOnly &&
					<div className="shop-priority-note">
						You&rsquo;re in early &mdash; {access.windowName || "priority sale"}.
					</div>}

				{onSale && <>
					<h2 className="shop-section-title">Tickets</h2>
					{types.length === 0 && <p className="shop-empty">No ticket types have been set up yet.</p>}
					{types.map((t) => (
						<div className="shop-ticket" key={t.ticketTypeId}>
							<span className="shop-ticket-name">{t.name}</span>
							<span className="shop-ticket-price">{money(t.pricePence)}</span>
							<div className="shop-stepper">
								<button aria-label={`One fewer ${t.name}`} disabled={!(quantities[t.ticketTypeId] > 0)} onClick={() => step(t.ticketTypeId, -1)} type="button">&minus;</button>
								<span>{quantities[t.ticketTypeId] || 0}</span>
								<button aria-label={`One more ${t.name}`} disabled={pickedTotal >= maxPerOrder} onClick={() => step(t.ticketTypeId, 1)} type="button">+</button>
							</div>
						</div>
					))}

					{data.maxPerOrder > 0 &&
						<p className="shop-note">
							{pickedTotal >= data.maxPerOrder
								? `That's the maximum of ${data.maxPerOrder} for this game.`
								: `Up to ${data.maxPerOrder} tickets per person for this game.`}
						</p>}

					{data.notes && <p className="shop-note">{data.notes}</p>}

					<div className="shop-sticky">
						<button className="shop-btn" disabled={!anyPicked} onClick={addToBasket} type="button">
							{anyPicked ? `Add to basket · ${money(total)}` : "Pick your tickets"}
						</button>
					</div>
				</>}
			</div>
			<ShopNav slug={slug} />
		</div>
	);
}
